import { useState } from "react"
import { X, Send, Loader2, AlertCircle } from "lucide-react"

const inputClass =
  "w-full px-3 py-2 h-10 border border-zinc-200 text-[14px] font-medium text-zinc-800 placeholder-zinc-500 focus:outline-none focus:border-zinc-500 transition-colors bg-white hover:border-zinc-300"

const labelClass =
  "block text-[11px] font-bold text-zinc-400 uppercase tracking-widest mb-1.5"

const ApplicationFormModal = ({
  questions,
  jobTitle,
  onSubmit,
  onClose,
  isSubmitting,
}) => {
  const [answers, setAnswers] = useState(
    questions.reduce((acc, q) => ({ ...acc, [q.id]: "" }), {}),
  )
  const [errors, setErrors] = useState({})

  const handleChange = (id, value) => {
    setAnswers((prev) => ({ ...prev, [id]: value }))
    setErrors((prev) => ({ ...prev, [id]: false }))
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    const newErrors = {}
    questions.forEach((q) => {
      if (!answers[q.id] || answers[q.id] === "") {
        newErrors[q.id] = true
      }
    })
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }
    onSubmit(answers)
  }

  const renderField = (question) => {
    const { id, type, options } = question

    switch (type) {
      case "number":
        return (
          <input
            type="number"
            value={answers[id]}
            onChange={(e) => handleChange(id, e.target.value)}
            placeholder="Enter a number"
            className={`${inputClass} ${errors[id] ? "border-red-400" : ""}`}
            style={{ borderRadius: 0 }}
          />
        )

      case "free_text":
        return (
          <textarea
            value={answers[id]}
            onChange={(e) => handleChange(id, e.target.value)}
            placeholder="Type your answer..."
            rows={3}
            className={`w-full px-3 py-2 border text-[14px] font-medium text-zinc-800 placeholder-zinc-500 focus:outline-none focus:border-zinc-500 transition-colors bg-white hover:border-zinc-300 resize-none ${errors[id] ? "border-red-400" : "border-zinc-200"}`}
            style={{ borderRadius: 0 }}
          />
        )

      case "multiple_choice":
        return (
          <div className="flex flex-wrap gap-2">
            {options.map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => handleChange(id, option)}
                className={`px-4 py-2 text-[11px] font-bold uppercase tracking-widest border transition-all ${
                  answers[id] === option
                    ? "bg-zinc-900 text-white border-zinc-900"
                    : errors[id]
                      ? "bg-white text-zinc-600 border-red-400 hover:border-red-500 hover:text-zinc-900"
                      : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400 hover:text-zinc-900"
                }`}
                style={{ borderRadius: 0 }}
              >
                {option}
              </button>
            ))}
          </div>
        )

      case "yes_no":
        return (
          <div
            className={`flex gap-0 border ${errors[id] ? "border-red-400" : "border-zinc-200"}`}
            style={{ borderRadius: 0 }}
          >
            {["Yes", "No"].map((option, idx) => (
              <button
                key={option}
                type="button"
                onClick={() => handleChange(id, option)}
                className={`flex-1 py-2 text-[11px] font-bold uppercase tracking-widest transition-all ${
                  idx === 0 ? "" : "border-l border-zinc-200"
                } ${
                  answers[id] === option
                    ? "bg-zinc-900 text-white"
                    : "bg-white text-zinc-600 hover:bg-zinc-50"
                }`}
                style={{ borderRadius: 0 }}
              >
                {option}
              </button>
            ))}
          </div>
        )

      default:
        return null
    }
  }

  const hasUnansweredFields = questions.some(
    (q) => !answers[q.id] || answers[q.id] === "",
  )

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 backdrop-blur-sm"
      style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
    >
      <style>{`@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500;600&display=swap');`}</style>

      <div
        className="bg-white w-full max-w-lg border border-zinc-200 shadow-2xl flex flex-col max-h-[92vh]"
        style={{ borderRadius: 0 }}
      >
        {/* Header */}
        <div className="flex justify-between items-center px-7 py-4 border-b border-zinc-200 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-1 h-6 bg-indigo-500" />
            <div>
              <h2 className="text-[13px] font-bold uppercase tracking-widest text-zinc-900">
                Before You Apply
              </h2>
              <p
                className="text-[11px] text-zinc-400 mt-0.5"
                style={{ fontFamily: "'IBM Plex Mono', monospace" }}
              >
                {jobTitle} · {questions.length} question
                {questions.length !== 1 ? "s" : ""} from employer
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="flex items-center justify-center w-7 h-7 border border-zinc-200 hover:border-zinc-400 hover:bg-zinc-50 text-zinc-400 hover:text-zinc-700 transition-all"
            style={{ borderRadius: 0 }}
          >
            <X size={14} />
          </button>
        </div>

        {/* Questions */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="px-7 py-5 overflow-y-auto flex-1 space-y-5">
            {questions.map((question, index) => (
              <div key={question.id}>
                <label className={labelClass}>
                  {index + 1}. {question.text}
                </label>
                {renderField(question)}
                {errors[question.id] && (
                  <p className="flex items-center gap-1 mt-1.5 text-[11px] font-bold text-red-500 uppercase tracking-widest">
                    <AlertCircle size={11} />
                    This field is required
                  </p>
                )}
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="px-7 py-4 border-t border-zinc-200 flex items-center justify-between shrink-0 bg-zinc-50">
            <p
              className="text-[13px] text-zinc-400"
              style={{ fontFamily: "'IBM Plex Mono', monospace" }}
            >
              All fields required
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="px-4 py-2 text-[11px] font-bold uppercase tracking-widest text-zinc-600 border border-zinc-200 hover:border-zinc-400 hover:text-zinc-900 transition-colors disabled:opacity-40"
                style={{ borderRadius: 0 }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex items-center gap-2 px-5 py-2 text-[11px] font-bold uppercase tracking-widest bg-zinc-900 text-white hover:bg-zinc-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed border border-zinc-900"
                style={{ borderRadius: 0 }}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 size={12} className="animate-spin" />
                    Submitting…
                  </>
                ) : (
                  <>
                    <Send size={12} strokeWidth={2.5} />
                    Submit Application
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

export default ApplicationFormModal
